import React, { useRef } from 'react'
import { Printer, Download, CheckCircle2 } from 'lucide-react'
import type { Transaction } from '@/types/pos'

interface BillPaymentReceiptProps {
  transaction: Transaction
  storeName?: string
  storeAddress?: string
  storePhone?: string
  storeEmail?: string
  receiptFooter?: string
  onClose: () => void
  onPrint?: () => void
  onNewPayment?: () => void
}

export const BillPaymentReceipt: React.FC<BillPaymentReceiptProps> = ({
  transaction,
  storeName = 'Sachin Poth Madura',
  storeAddress = '',
  storePhone = '',
  receiptFooter = 'Thank you for your payment!',
  onClose,
  onPrint,
  onNewPayment,
}) => {
  const receiptRef = useRef<HTMLDivElement>(null)

  const billType = transaction.billType || (transaction.items as any[])?.[0]?.productName || 'Bill Payment'
  const provider = transaction.billProvider || ''
  const accountRef = transaction.billAccountRef || ''
  const reference = transaction.billReference || ''
  const total = Number(transaction.total) || 0
  const fee = Number((transaction as any).serviceFee) || 0
  const billAmount = fee > 0 ? total - fee : total

  const handlePrint = () => {
    const printWindow = window.open('', '_blank')
    if (printWindow && receiptRef.current) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Bill Payment - ${transaction.receiptNumber || transaction.transactionNumber}</title>
            <style>
              @media print { @page { margin: 2mm 5mm; size: 80mm auto; } body { margin: 0; padding: 4mm 6mm; } }
              body { font-family: Arial, sans-serif; font-size: 12px; line-height: 1.4; max-width: 80mm; margin: 0 auto; padding: 4mm 6mm; color: #000; }
              .header { text-align: center; margin-bottom: 12px; }
              .header h1 { margin: 0; font-size: 16px; font-weight: bold; }
              .header .invoice-title { font-size: 14px; font-weight: bold; margin: 8px 0; letter-spacing: 0.5px; }
              .divider { border-top: 1px dashed #000; margin: 10px 0; }
              .row { display: flex; justify-content: space-between; margin: 4px 0; font-size: 11px; }
              .row .label { color: #444; }
              .total-row { font-weight: bold; font-size: 14px; border-top: 2px solid #000; padding-top: 8px; margin-top: 8px; }
              .footer { text-align: center; margin-top: 16px; font-size: 10px; }
            </style>
          </head>
          <body>
            <div class="header">
              <h1>${storeName}</h1>
              ${storeAddress ? `<p style="margin:2px 0;font-size:10px">${storeAddress}</p>` : ''}
              ${storePhone ? `<p style="margin:2px 0;font-size:10px">Tel: ${storePhone}</p>` : ''}
              <p class="invoice-title">BILL PAYMENT INVOICE</p>
              <p style="font-size:10px">${new Date(transaction.createdAt).toLocaleString()}</p>
            </div>
            <div class="divider"></div>
            <div class="row"><span class="label">Invoice #</span><span>${transaction.receiptNumber || transaction.transactionNumber}</span></div>
            <div class="row"><span class="label">Bill Type</span><span>${billType}</span></div>
            ${provider ? `<div class="row"><span class="label">Provider</span><span>${provider}</span></div>` : ''}
            ${accountRef ? `<div class="row"><span class="label">Account / Ref</span><span>${accountRef}</span></div>` : ''}
            ${reference ? `<div class="row"><span class="label">Reference</span><span>${reference}</span></div>` : ''}
            ${transaction.customerName ? `<div class="row"><span class="label">Customer</span><span>${transaction.customerName}</span></div>` : ''}
            <div class="divider"></div>
            ${fee > 0 ? `
              <div class="row"><span class="label">Bill Amount</span><span>Rs.${billAmount.toFixed(2)}</span></div>
              <div class="row"><span class="label">Service Fee</span><span>Rs.${fee.toFixed(2)}</span></div>
            ` : ''}
            <div class="row total-row"><span>Total Paid</span><span>Rs.${total.toFixed(2)}</span></div>
            <div class="row"><span class="label">Payment</span><span>${String(transaction.paymentMethod || 'cash').toUpperCase()}</span></div>
            <div class="divider"></div>
            <div class="footer">${receiptFooter}</div>
          </body>
        </html>
      `)
      printWindow.document.close()
      printWindow.focus()
      setTimeout(() => {
        printWindow.print()
        printWindow.close()
      }, 250)
    }
    onPrint?.()
  }

  const handleDownload = () => {
    const content = `
${storeName}
${storeAddress}
${storePhone ? `Tel: ${storePhone}` : ''}

BILL PAYMENT INVOICE
${new Date(transaction.createdAt).toLocaleString()}

Invoice #: ${transaction.receiptNumber || transaction.transactionNumber}
Bill Type: ${billType}
${provider ? `Provider: ${provider}` : ''}
${accountRef ? `Account/Ref: ${accountRef}` : ''}
${reference ? `Reference: ${reference}` : ''}
${transaction.customerName ? `Customer: ${transaction.customerName}` : ''}

${'─'.repeat(40)}
${fee > 0 ? `Bill Amount: Rs.${billAmount.toFixed(2)}\nService Fee: Rs.${fee.toFixed(2)}\n` : ''}Total Paid: Rs.${total.toFixed(2)}
Payment: ${String(transaction.paymentMethod || 'cash').toUpperCase()}
${'─'.repeat(40)}

${receiptFooter}
    `.trim()
    const blob = new Blob([content], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `bill-payment-${transaction.receiptNumber || transaction.transactionNumber}.txt`
    a.click()
    URL.revokeObjectURL(url)
  }

  const paymentLabel = String(transaction.paymentMethod || 'cash').replace(/_/g, ' ').toUpperCase()

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="glass-card max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-10 h-10 text-green-500" />
          </div>
          <h2 className="text-2xl font-bold esales-text">Payment Successful</h2>
          <p className="text-muted-foreground mt-1">Invoice #{transaction.receiptNumber || transaction.transactionNumber}</p>
        </div>

        <div ref={receiptRef} className="bg-white text-black p-6 rounded-lg text-sm mb-6 shadow-inner" style={{ fontFamily: 'Arial, sans-serif' }}>
          <div className="text-center mb-4">
            <h4 className="font-bold text-lg">{storeName}</h4>
            {storeAddress && <p className="text-xs">{storeAddress}</p>}
            {storePhone && <p className="text-xs">Tel: {storePhone}</p>}
            <p className="text-sm font-bold mt-2 tracking-wide">BILL PAYMENT INVOICE</p>
            <p className="text-xs mt-1">{new Date(transaction.createdAt).toLocaleString()}</p>
          </div>
          <div className="border-t border-b border-gray-300 py-3 my-3 space-y-1">
            <p className="text-xs"><strong>Invoice #:</strong> {transaction.receiptNumber || transaction.transactionNumber}</p>
            <p className="text-xs"><strong>Bill Type:</strong> {billType}</p>
            {provider && <p className="text-xs"><strong>Provider:</strong> {provider}</p>}
            {accountRef && <p className="text-xs"><strong>Account / Ref:</strong> {accountRef}</p>}
            {reference && <p className="text-xs"><strong>Reference:</strong> {reference}</p>}
            {transaction.customerName && <p className="text-xs"><strong>Customer:</strong> {transaction.customerName}</p>}
          </div>
          <div className="border-t border-gray-300 pt-3 mt-3 space-y-1">
            {fee > 0 && (
              <>
                <div className="flex justify-between text-xs">
                  <span>Bill Amount</span>
                  <span>Rs.{billAmount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span>Service Fee</span>
                  <span>Rs.{fee.toFixed(2)}</span>
                </div>
              </>
            )}
            <div className="flex justify-between font-bold text-base border-t border-gray-300 pt-2">
              <span>Total Paid</span>
              <span>Rs.{total.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-xs mt-2">
              <span>Payment Method</span>
              <span>{paymentLabel}</span>
            </div>
          </div>
          <div className="text-center mt-6 border-t border-gray-300 pt-3">
            <p className="text-xs">{receiptFooter}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-4">
          <button type="button" onClick={handlePrint} className="flex items-center justify-center gap-2 p-4 bg-primary/10 hover:bg-primary/20 rounded-xl transition-colors">
            <Printer className="w-5 h-5 text-primary" />
            <span className="font-semibold text-sm">Print</span>
          </button>
          <button type="button" onClick={handleDownload} className="flex items-center justify-center gap-2 p-4 bg-blue-500/10 hover:bg-blue-500/20 rounded-xl transition-colors">
            <Download className="w-5 h-5 text-blue-500" />
            <span className="font-semibold text-sm">Download</span>
          </button>
        </div>

        <div className="flex gap-3">
          <button type="button" onClick={onClose} className="flex-1 py-3 bg-muted hover:bg-muted/80 rounded-xl font-semibold text-sm transition-colors">
            Close
          </button>
          <button
            type="button"
            onClick={() => { onNewPayment?.(); onClose() }}
            className="flex-1 py-3 bg-primary text-primary-foreground rounded-xl font-semibold text-sm glow-effect transition-colors"
          >
            New Payment
          </button>
        </div>
      </div>
    </div>
  )
}
